import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ImageUpload from '../components/ImageUpload';
import '../App.css';

/**
 * HomePage Component
 * Main search/upload page
 */
const HomePage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [hasPreview, setHasPreview] = useState(false);

  /**
   * Handle search completion from ImageUpload component
   * Navigate to results page with all data
   */
  const handleSearchComplete = (data) => {
    // Data includes: results, originalImage, enhancedImage, etc.
    const {
      results,
      originalImage,
      enhancedImage,
      backgroundRemovedImage,
      showBackgroundRemoved,
      selectedFileName
    } = data;

    // Extract results from API response (handle different formats)
    let resultsArray = [];

    if (Array.isArray(results)) {
      resultsArray = results;
    } else if (results && Array.isArray(results.results)) {
      resultsArray = results.results;
    } else if (results && Array.isArray(results.matches)) {
      resultsArray = results.matches;
    }

    // Normalize results to consistent format (same logic as ResultsPage)
    const normalizedResults = resultsArray.map((result, index) => {
      // Calculate confidence as percentage
      // score is similarity (0-1 range, higher is better) from Matching Engine
      let confidence = 0;
      if (result.score !== undefined) {
        // score is similarity value (0-1), convert to percentage
        // Higher score = better match (0.9999 = 99.99% for exact match)
        confidence = result.score * 100;
      } else if (result.similarity !== undefined && result.similarity !== null) {
        // Fallback to similarity field if score not available
        confidence = result.similarity;
      } else if (result.confidence !== undefined) {
        confidence = result.confidence;
      }

      return {
        sku: result.sku || result.id || `ITEM-${index + 1}`,
        confidence: confidence,
        category: result.category || result.type || 'Jewelry',
        metadata: result.metadata || result.details || {},
        imageUrl: result.imageUrl,
      };
    });

    // Navigate to results page with state
    navigate('/results', {
      state: {
        originalImage,
        enhancedImage,
        backgroundRemovedImage,
        showBackgroundRemoved,
        searchResults: normalizedResults,
        selectedFileName,
      }
    });
  };

  /**
   * Handle errors from ImageUpload component
   */
  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  /**
   * Dismiss error message
   */
  const dismissError = () => {
    setError(null);
  };

  /**
   * Handle history button click
   */
  const handleHistoryClick = () => {
    navigate('/history');
  };

  return (
    <div className="app">
      {/* Navigation Header */}
      <Header onHistoryClick={handleHistoryClick} />

      {/* Page Title Section */}
      <section className="page-header"></section>

      {/* Main Content Container */}
      <main className="app-main">
        {/* Error Banner (dismissible) */}
        {error && (
          <div className="error-banner">
            <div className="error-content">
              <span className="error-icon">⚠️</span>
              <span className="error-message">{error}</span>
            </div>
            <button
              className="error-dismiss"
              onClick={dismissError}
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        )}

        {/* Upload Section */}
        <section className={`upload-section ${hasPreview ? 'has-preview' : ''}`}>
          <ImageUpload
            onSearchComplete={handleSearchComplete}
            onError={handleError}
            onPreviewChange={setHasPreview}
          />
        </section>
      </main>
    </div>
  );
};

export default HomePage;
